import { FormInstance, message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import axios from 'axios';
import { DEFAULT_LANGUAGE, DEFAULT_ORIENTATION, THUMBNAIL_UPLOAD_STATE } from 'constants/index';
import { seasonService } from 'content/service';
import { ThumbnailInterface } from 'content/type';
import { Dispatch, SetStateAction } from 'react';
import { PosterInterface, UploadPosterInterface } from 'type/seasonType';

interface PosterHandlersProps {
  form: FormInstance;
  seasonId: number;
  selectedPoster: PosterInterface | null;
  setSelectedPoster: (poster: PosterInterface | null) => void;
  setNewPosterList: (posterList: PosterInterface[]) => void;
  setUploadedPosterList: (posterList: PosterInterface[]) => void;
  removedUids: string[];
  setRemovedUids: Dispatch<SetStateAction<string[]>>;
}

interface PosterHandlers {
  handleChange: (info: UploadChangeParam<UploadFile<UploadPosterInterface>>) => void;
  handlePosterSelect: (poster: PosterInterface | null) => void;
  handlePosterUpload: () => void;
  handleNewPosterRemove: (poster: PosterInterface) => void;
  handleUploadedPosterRemove: (poster: PosterInterface) => void;
  handlePosterChange: (poster: PosterInterface, key: string, value: string) => void;
  handleNewPosterChange: (poster: PosterInterface, key: string, value: string) => void;
}

const createUploadPosterList = (
  uploadFileList: UploadFile<UploadPosterInterface>[]
): PosterInterface[] => {
  let fileId = 0;
  return uploadFileList.map((file: UploadFile<UploadPosterInterface>): PosterInterface => {
    const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
    return {
      id: fileId++,
      uid: file.uid,
      file: fileUrl,
      is_representative: false,
      state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
      originFileObj: file.originFileObj,
      orientation: DEFAULT_ORIENTATION,
      language: DEFAULT_LANGUAGE,
    };
  });
};

const handleChange =
  (props: PosterHandlersProps) =>
  (info: UploadChangeParam<UploadFile<UploadPosterInterface>>): void => {
    const { form, setNewPosterList, removedUids } = props;

    // 1) form에 저장된 기존 newPosterList 꺼내오기
    const existingList: PosterInterface[] = form.getFieldValue('newPosterList') || [];

    // 2) 기존 리스트에 담긴 uid만 뽑아서 배열로
    const existingUids = existingList.map(p => p.uid).filter((uid): uid is string => !!uid);

    // 3) info.fileList 중, 삭제된 uid도 아니고, 기존에 이미 추가된 uid도 아닌 것만 골라내기
    const filtered = (info.fileList || []).filter(
      file => !removedUids.includes(file.uid) && !existingUids.includes(file.uid)
    );

    // 4) 새로 올라온 파일만 PosterInterface로 변환
    const uploadPosterList = createUploadPosterList(
      filtered as UploadFile<UploadPosterInterface>[]
    );

    // 5) 기존 리스트와 합쳐서 최종 리스트 만들기
    const combined = [...existingList, ...uploadPosterList];

    // 6) form과 state 동기화
    form.setFieldValue('newPosterList', combined);
    setNewPosterList(combined);
  };

const handlePosterSelect =
  (props: PosterHandlersProps) =>
  (newSelectedPoster: PosterInterface | null): void => {
    const { form, selectedPoster, setSelectedPoster } = props;
    if (selectedPoster) {
      selectedPoster.is_representative = false;
    }
    if (newSelectedPoster) {
      newSelectedPoster.is_representative = true;
    }
    form.setFieldValue('selectedPoster', newSelectedPoster);
    setSelectedPoster(newSelectedPoster);
  };

const handlePosterUpload = (props: PosterHandlersProps) => async (): Promise<void> => {
  const { form, seasonId } = props;
  const loadingMessage = message.loading('Creating...', 0);
  const posterList = form.getFieldValue('posterList');
  const selectedPoster = form.getFieldValue('selectedPoster');
  // 1️⃣ 폼에서 새로 추가된 것과 기존 업로드된 것 가져오기
  const newPosterList: PosterInterface[] = form.getFieldValue('newPosterList') || [];
  const uploadedPosterList: PosterInterface[] = form.getFieldValue('uploadedPosterList') || [];

  // 2️⃣ 중복 조합 체크
  const allList = [...newPosterList, ...uploadedPosterList];
  const comboCounts = allList.reduce<Record<string, number>>((acc, t) => {
    const key = `${t.language}-${t.orientation}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const duplicates = Object.entries(comboCounts)
    .filter(([_, cnt]) => cnt > 1)
    .map(([combo]) => combo);

  if (duplicates.length > 0) {
    loadingMessage(); // 로딩 인디케이터 닫기
    message.error(`중복된 언어/화면비 조합: ${duplicates.join(', ')}`);
    return;
  }

  if (selectedPoster) {
    newPosterList.map((file: { id: number; is_representative: boolean }) => {
      if (file.id === selectedPoster.id) {
        file.is_representative = selectedPoster.is_representative;
        selectedPoster.id = -1;
      }
      return file;
    });
  }

  const removePosters = posterList.filter(
    (uploadedPoster: { id: number }) =>
      !uploadedPosterList.some(
        (uploadPoster: { id: number }) => uploadPoster.id === uploadedPoster.id
      )
  );

  const changedPosters = uploadedPosterList.filter((updated: ThumbnailInterface) => {
    const original = posterList.find((t: ThumbnailInterface) => t.id === updated.id);
    return (
      original &&
      (original.language !== updated.language ||
        original.orientation !== updated.orientation ||
        original.is_representative !== updated.is_representative)
    );
  });

  try {
    if (newPosterList.length > 0) {
      await seasonService.uploadPoster(seasonId, newPosterList);
    }
    if (removePosters.length > 0) {
      await seasonService.removePoster(seasonId, removePosters);
    }
    if (changedPosters.length > 0) {
      for (const poster of changedPosters) {
        await seasonService.updatePoster(seasonId, poster);
      }
    }
    if (selectedPoster && selectedPoster.id !== -1) {
      await seasonService.updatePoster(seasonId, selectedPoster);
    }
    message.success('upload success');
    window.location.reload();
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      message.error('Failed to update : ' + JSON.stringify(error.response.data));
    }
  }
  loadingMessage();
};

const handleNewPosterChange =
  (props: PosterHandlersProps) =>
  (targetPoster: UploadPosterInterface, key: string, value: string): void => {
    const { form, setNewPosterList } = props;
    const newPosterList = form.getFieldValue('newPosterList') || [];

    const updatedList = newPosterList.map((poster: UploadPosterInterface) => {
      if (poster.uid !== targetPoster.uid) return poster;

      const updated: UploadPosterInterface = {
        ...poster,
        [key]: value,
      };

      return updated;
    });

    // ✅ 1. form 값에는 원본 그대로 저장 (originFileObj 유지)
    form.setFieldValue('newPosterList', updatedList);

    // ✅ 2. 화면 렌더링용은 file을 blob URL로 보장
    const renderList = updatedList.map((poster: UploadPosterInterface) => ({
      ...poster,
      file:
        typeof poster.file !== 'string' && poster.file
          ? URL.createObjectURL(poster.file)
          : poster.file,
    }));

    setNewPosterList(renderList);
  };

const handlePosterChange =
  (props: PosterHandlersProps) =>
  (targetPoster: PosterInterface, key: string, value: string): void => {
    const { form, setUploadedPosterList } = props;
    const uploadedPosterList = form.getFieldValue('uploadedPosterList') || [];

    const updatedList = uploadedPosterList.map((poster: PosterInterface) =>
      poster.id === targetPoster.id ? { ...poster, [key]: value } : poster
    );

    form.setFieldValue('uploadedPosterList', updatedList);
    setUploadedPosterList(updatedList);
  };

const handleNewPosterRemove =
  (props: PosterHandlersProps) =>
  (removedPoster: PosterInterface): void => {
    const { form, setNewPosterList, removedUids, setRemovedUids } = props;
    const newPosterList = form.getFieldValue('newPosterList') || [];

    const tempNewPosterList = newPosterList.filter(
      (poster: { uid: string }) => poster.uid !== removedPoster.uid
    );

    form.setFieldValue('newPosterList', tempNewPosterList);
    setNewPosterList(tempNewPosterList);

    if (removedPoster.uid) {
      setRemovedUids([...removedUids, removedPoster.uid]);
    }
  };

const handleUploadedPosterRemove =
  (props: PosterHandlersProps) =>
  (removedPoster: PosterInterface): void => {
    const { form, setUploadedPosterList } = props;
    const uploadedPosterList = form.getFieldValue('uploadedPosterList') || [];

    const tempUploadedPosterList = uploadedPosterList.filter(
      (poster: { id: number }) => poster.id !== removedPoster.id
    );

    form.setFieldValue('uploadedPosterList', tempUploadedPosterList);
    setUploadedPosterList(tempUploadedPosterList);
  };

const posterHandlers = (props: PosterHandlersProps): PosterHandlers => ({
  handleChange: handleChange(props),
  handlePosterSelect: handlePosterSelect(props),
  handlePosterUpload: handlePosterUpload(props),
  handlePosterChange: handlePosterChange(props),
  handleNewPosterChange: handleNewPosterChange(props),
  handleNewPosterRemove: handleNewPosterRemove(props),
  handleUploadedPosterRemove: handleUploadedPosterRemove(props),
});

export default posterHandlers;
